import type { Product, PriceInfo } from "./product-types";
import { dataKey } from "./product-types";
import type { PromotionData, PromoAction, PromoStats } from "./promotion-types";
import { mskDateString } from "./refresh/dates";

export type PromoPresenceItem = {
  action: PromoAction;
  stats: PromoStats;
  product: Product;
};

export type PromoAvailabilityItem = {
  action: PromoAction;
  product: Product;
  participating: boolean;
  price: number | null;
  discountPct: number | null;
  minStock: number | null;
};

export function promoKeys(product: Product) {
  const key = dataKey(product);
  return key === product.offer_id ? [product.offer_id] : [product.offer_id, key];
}

function dateShort(value: string) {
  return value ? value.slice(0, 10) : "";
}

export function isPromoActionActiveNow(action: PromoAction | undefined, nowMs: number = Date.now()) {
  if (!action) return true;
  const today = mskDateString(nowMs);
  const from = dateShort(action.date_start);
  const to = dateShort(action.date_end);
  return (!from || from <= today) && (!to || to >= today);
}

export function promoItemsForProduct(product: Product, promoData?: PromotionData | null): PromoPresenceItem[] {
  if (!promoData) return [];
  const actionById = new Map(promoData.actions.map(action => [String(action.id), action]));
  const byAction = promoKeys(product).flatMap(key => Object.entries(promoData.byOffer?.[key] ?? {}));
  return byAction
    .filter(([actionId, stats]) => stats.is_participating && isPromoActionActiveNow(actionById.get(actionId)))
    .map(([actionId, stats]) => ({
      action: actionById.get(actionId) ?? { id: Number(actionId), title: actionId, date_start: "", date_end: "", action_type: "" },
      stats,
      product,
    }));
}

function currentProductPrice(product: Product, priceInfo?: Partial<PriceInfo> | null) {
  return priceInfo?.marketing_price ?? (Number.isFinite(parseFloat(product.price)) ? parseFloat(product.price) : 0);
}

export function promoAvailabilityForProduct(
  product: Product,
  promoData?: PromotionData | null,
  priceInfo?: Partial<PriceInfo> | null,
): PromoAvailabilityItem[] {
  if (!promoData) return [];
  const keys = promoKeys(product);
  const currentPrice = currentProductPrice(product, priceInfo);
  return (promoData.actions ?? [])
    .filter(action => isPromoActionActiveNow(action))
    .flatMap(action => {
      const actionId = String(action.id);
      const stats = keys.map(key => promoData.byOffer?.[key]?.[actionId]).find(Boolean);
      const participating = !!stats?.is_participating;
      const available = keys.some(key => promoData.candidates?.[actionId]?.includes(key));
      if (!participating && !available) return [];

      const candidatePrice = keys
        .map(key => promoData.candidatePrices?.[actionId]?.[key])
        .find(price => price != null && price > 0);
      const price = stats?.action_price || stats?.max_action_price || candidatePrice || null;
      const discountPct = price && currentPrice ? Math.round((1 - price / currentPrice) * 100) : null;
      return [{
        action,
        product,
        participating,
        price,
        discountPct,
        minStock: stats?.min_stock ?? null,
      }];
    });
}

export function collectPromoPresenceItems(products: Product[], promoData?: PromotionData | null) {
  return products.flatMap(product => promoItemsForProduct(product, promoData));
}

export function collectPromoAvailabilityItems(
  products: Product[],
  promoData?: PromotionData | null,
  priceData?: Record<string, Partial<PriceInfo>>,
) {
  return products.flatMap(product => promoAvailabilityForProduct(product, promoData, priceData?.[dataKey(product)]));
}

export function hasActivePromotion(product: Product, promoData?: PromotionData | null) {
  return promoItemsForProduct(product, promoData).length > 0;
}

export function countAvailablePromotions(product: Product, promoData?: PromotionData | null, priceInfo?: Partial<PriceInfo> | null) {
  return promoAvailabilityForProduct(product, promoData, priceInfo).length;
}
